import { create } from 'zustand';
/**
 * 서버 저장(대시보드 v2 V10) — **깃발 뒤**다. `VITE_DASHBOARD_SERVER_LAYOUT` 이 꺼져 있으면
 * 아래 두 부름은 첫 줄에서 돌아가므로 저장 · 화면 · 네트워크가 지금과 한 글자도 다르지 않다.
 */
import { syncOnHydrate, schedulePush, type SyncPorts } from './dashboard-layout-controller';

/**
 * 대시보드 위젯 보드 store — all-frame.html #2a·#2b·#2d.
 *
 * 담는 것: 보드 목록 / 활성 보드 / 보드별 위젯 배치 / 편집 모드.
 * 담지 않는 것: 위젯의 **데이터**. 그건 `useWidgetData` + `widget-cache` 소관이다
 *   (배치가 바뀔 때마다 데이터를 다시 긁지 않도록 의도적으로 분리).
 *
 * 영속: localStorage(사용자별 키). 서버 저장은 후속 — 저장소 교체 시 본 파일의
 * `load/save` 만 갈아끼우면 되도록 나머지 코드는 저장 방식을 모르게 두었다.
 *
 * 위젯 key 는 `string` 이다(레지스트리 타입을 import 하지 않는다):
 *   1) store → registry → 위젯 컴포넌트 → store 순환 import 를 끊고,
 *   2) 저장된 배치에 지금은 없는 위젯 key 가 남아 있어도 렌더 시 조용히 건너뛴다.
 */

/**
 * 회사가 정한 기본판 한 장 (migration 173 · 시안 화면 I).
 *
 * ⚠ 서버 DTO 를 그대로 import 하지 않는다 — store 가 api 모듈에 매이면 검사가 늘 네트워크를
 *   흉내 내야 한다. **필요한 칸만** 여기 적는다(공용 어휘는 `api/dashboard-templates`).
 */
export interface CompanyTemplate {
  kind: 'CORP' | 'NEW_USER' | 'ORG';
  name: string;
  widgets: Array<{ key: string; span: number }>;
  enabled: boolean;
  loginLanding: 'CORP' | 'PERSONAL' | 'LAST';
  forcedRevision: number;
  /** 사용자가 탭을 숨길 수 있는가 (migration 175). 서버가 안 주면 «허용» 이 기본이다. */
  userCanHide?: boolean;
  /** FIRST 면 탭 순서 바꾸기가 잠긴다 (migration 175). */
  tabPosition?: 'FIRST' | 'FREE';
}

export type BoardKind = 'corp' | 'personal' | 'team' | 'project';
export type WidgetSpan = 4 | 6 | 8 | 12;
export type BoardVisibility = 'private' | 'team' | 'members';

/**
 * «그 자리에 놓인 위젯»의 설정 (2026-09-08 · 시안 화면 C).
 *
 * ★ 위젯이 아니라 **배치**의 값이다 — 같은 위젯을 두 자리에 놓으면 이름도 높이도 다를 수 있다.
 * ★ 선택 칸이라 옛 저장본도 그대로 읽힌다(없으면 기본값).
 */
export interface WidgetSettings {
  /** 사용자가 고친 표시 이름. 비면 위젯이 제 이름을 쓴다. */
  title?: string;
  /** 제목을 보일지. 그림이 주인공인 위젯은 숨겨서 통으로 쓴다. */
  showTitle?: boolean;
  height?: 's' | 'm' | 'l';

  /* ── 위젯 고유 설정 (2026-09-09 · 판 5) ────────────────────────────────
     ★★★ 여기 있는 셋은 **켜도 아무 일이 없는 스위치가 아니다** — 값이 없으면 그 위젯이
         「무엇을 보여 줄지 모르는」 상태라, 위젯이 빈 그림을 지어내는 대신 «고르세요» 라고
         적는다. 판 2 에서 미뤄 둔 스위치들과 다른 점이 그것이다.
     ★ 전부 선택 칸이라 옛 저장본이 그대로 읽힌다. 개인 배치와 함께 **브라우저에** 남는다. */
  /** 포토월이 그릴 사진이 든 워크스페이스 폴더. */
  photoFolderId?: string;
  /** 그 폴더의 이름 — 설정 창이 「무엇을 골랐는지」를 되보여 준다(id 만으로는 못 읽는다). */
  photoFolderName?: string;
  /** 빠른 실행에 놓을 바로가기 key 목록(`quick-links.ts` 의 카탈로그). */
  quickLinks?: string[];
  /** 회의실 현황이 볼 자원 분류. 비면 전부. */
  resourceCategory?: string;
  /**
   * 캘린더 위젯에서 **날짜를 누르면** 무엇을 할지 (2026-09-16 · 사용자 요청).
   *
   * ★ 없으면 `'expand'` — 달력 **아래에 그날 일정을 편다**(기본값).
   * ★ `'navigate'` 는 **종전 동작 그대로** 캘린더 화면으로 간다 — 이동하던 길을 없애지 않는다.
   * ★ `'none'` 은 아무 일도 하지 않는다.
   */
  dayClick?: 'expand' | 'navigate' | 'none';
  /**
   * 날씨 위젯의 «어디 날씨» (2026-09-21 · 시안 v4).
   *
   * ★ 없거나 `'mine'` 이면 **내 사업장, 없으면 회사** — 그 판단은 **서버**가 한다
   *   (화면은 제 근무지를 모른다). `'company'` 는 회사, 그 밖은 사업장 id 다.
   * ★ 사업장 하나를 콕 집는 것은 **사업장 목록을 볼 수 있는 사람만** 할 수 있다(설정 창이 가린다).
   */
  weatherPlace?: WeatherPlaceSetting;

  /* ── 보이는 항목 · 선택지 (2026-09-15 · 대시보드 v2 V1) ───────────────────
     ★★★ 무엇이 어떤 항목인지는 `pages/DashboardPage/board/widget-items.ts` 가 정한다 — 여기는 값만.
     ★★ 둘 다 선택 칸이다. 저장 모양의 `v` 는 **1 그대로** 둔다 — 옛 판(`v !== 1` 이면 통째로 버림)이
        열어도 배치가 살아 있게. 옛 판은 모르는 칸을 스프레드로 그대로 들고 다닌다.
     ★ 없으면 위젯은 지금 그리던 그대로다(기본값 = 현행 화면). */
  /** 보이는 항목 — **배열 순서가 곧 표시 순서**다. 모르는 key 도 지우지 않고 들고 있는다. */
  items?: WidgetItemSetting[];
  /** 개수 · 기본 필터 · 기간 · 범위 같은 선택지(key → 값). 잘못된 값은 읽을 때 기본값으로 떨어진다. */
  options?: Record<string, WidgetOptionValue>;
}

/** 보이는 항목 하나의 저장값 (대시보드 v2 V1). */
export interface WidgetItemSetting {
  key: string;
  on: boolean;
}

/** 선택지 값 — 하나 고르기(string) · 여럿 고르기(string[]) · 켜고 끄기(boolean). */
export type WidgetOptionValue = string | boolean | string[];

/**
 * 날씨 위젯의 「어디 날씨」 (2026-09-21 · 시안 v4).
 *   'mine'(기본 · 내 사업장, 없으면 회사) | 'company' | 사업장 id.
 * ★ 기본값의 판단은 **서버**가 한다 — 화면은 제 근무지를 모른다(프로필 응답에 근무지가 없다).
 */
export type WeatherPlaceSetting = string;

/**
 * 대시보드 색 (2026-09-09 · 시안 화면 H 의 「이름 옆 색 고르기」).
 *
 * ★★★ **탭의 점이 이 색을 쓴다.** 종전에는 점이 `kind`(개인·부서·전사) 색이라 **부서 대시보드
 *     둘을 나란히 두면 점이 똑같았다** — 시안이 색을 «고르게» 만든 이유다.
 * ★ 안 고르면 종전대로 `kind` 색이다(회귀 0).
 */
export const BOARD_COLORS = [
  { value: 'blue', token: 'var(--accent-blue)', label: '파랑' },
  { value: 'green', token: 'var(--accent-green)', label: '초록' },
  { value: 'purple', token: 'var(--accent-purple)', label: '보라' },
  { value: 'orange', token: 'var(--accent-orange)', label: '주황' },
  { value: 'pink', token: 'var(--accent-pink)', label: '분홍' },
  { value: 'teal', token: 'var(--accent-teal)', label: '청록' },
  { value: 'red', token: 'var(--accent-red)', label: '빨강' },
  { value: 'gray', token: 'var(--text-subtle)', label: '회색' },
] as const;

export type BoardColor = (typeof BOARD_COLORS)[number]['value'];

export function boardColorToken(color?: string | null): string | null {
  return BOARD_COLORS.find((c) => c.value === color)?.token ?? null;
}

export interface BoardWidget {
  /** 배치 인스턴스 id — 같은 위젯을 두 번 얹을 수 있으므로 key 와 별개. */
  id: string;
  widgetKey: string;
  span: WidgetSpan;
  settings?: WidgetSettings;
}

export interface DashboardBoard {
  id: string;
  name: string;
  kind: BoardKind;
  visibility: BoardVisibility;
  /** 탭 점 색. 안 고르면 `kind` 색을 쓴다(2026-09-09 · 시안 화면 H). */
  color?: BoardColor;
  /** 회사 담당자 고정형 — 사용자는 보기만 (#2b). */
  locked?: boolean;
  widgets: BoardWidget[];
}

interface PersistedShape {
  v: 1;
  activeBoardId: string;
  boards: DashboardBoard[];
  /**
   * 회사 기본판을 «어느 회차까지 받았는가» (2026-09-08 · migration 173).
   *
   * ★ 선택 칸이다 — 이 값이 없는 옛 저장본도 그대로 읽힌다(v 는 1 그대로).
   * ★ 이것이 없으면 「덮어쓰기」를 누를 때마다 열 때마다 배치가 날아간다.
   */
  seenForced?: Record<string, number>;
  /** 탭에서 숨긴 보드 (2026-09-08 · 시안 화면 H). 지운 것이 아니라 **접어 둔 것**이다. */
  hiddenBoardIds?: string[];
  /** 로그인하면 여기서 시작한다 — 「기본으로 지정」(시안 화면 H). */
  defaultBoardId?: string;
}

const STORAGE_PREFIX = 'axi.dashboard.v1';

let seq = 0;
/** 렌더 사이에 안정적인 로컬 id. 서버 id 와 섞이지 않도록 접두어를 둔다. */
function localId(prefix: string): string {
  seq += 1;
  return `${prefix}-${Date.now().toString(36)}-${seq.toString(36)}`;
}

function w(widgetKey: string, span: WidgetSpan = 4): BoardWidget {
  return { id: localId('w'), widgetKey, span };
}

/** 최초 진입 기본 보드 — all-frame #2a(나의)·#2b(전사) 위젯 구성 그대로. */
/**
 * 앱에 박힌 기본 보드.
 *
 * ★★★ **「전사 대시보드」는 여기 없다** (2026-09-09). 전사 탭은 **회사가 정하는 것**이라,
 *     회사가 정하지 않았는데 앱이 지어낸 전사 탭이 위젯을 달고 떠 있으면
 *     **정한 적 없는 것이 정해진 것처럼** 보인다(사용자가 그것을 보고 물었다).
 *     통합설정 › 위젯 관리에서 배치를 저장하면 그때 `applyTemplates` 가 이 탭을 세운다.
 */
function defaultBoards(): DashboardBoard[] {
  /*
   * ★★★ **아무것도 없다** (2026-09-09 사용자 지시 — 「초기 상태는 나의 대시보드도 없어야 한다」).
   *
   *   앱이 「나의 대시보드」·「부서 대시보드」·「프로젝트 대시보드」를 미리 지어 두고 위젯까지
   *   채워 두면, 그것은 **아무도 정한 적 없는 화면**이다 — 회사도 사용자도 고른 적이 없다.
   *   · 회사가 정한 것 = **전사 탭**(통합설정 › 위젯 관리 → `applyTemplates` 가 세운다)
   *   · 사용자가 정한 것 = **본인이 만든 탭**(「+」 로 만든다)
   *   그 둘 말고는 없다. 처음 여는 사람은 **빈 자리와 「만들기」**를 본다.
   *
   * ⚠ 이미 쓰던 사람의 저장본은 그대로 산다 — `load()` 가 있으면 그것을 쓰고, 이 함수는
   *   **저장본이 없을 때만** 불린다.
   */
  return [];
}

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}.${userId || 'anon'}`;
}

function load(userId: string): PersistedShape | null {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedShape;
    if (parsed?.v !== 1 || !Array.isArray(parsed.boards) || parsed.boards.length === 0) return null;
    return parsed;
  } catch {
    return null;
  }
}

function save(
  userId: string, boards: DashboardBoard[], activeBoardId: string,
  seenForced?: Record<string, number>, hiddenBoardIds?: string[], defaultBoardId?: string,
): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify({
      v: 1, boards, activeBoardId, seenForced, hiddenBoardIds, defaultBoardId,
    } satisfies PersistedShape));
  } catch {
    /* 저장 실패(용량·프라이빗 모드)는 무시 — 화면 동작에는 영향 없음. */
  }
}

interface DashboardState {
  hydratedFor: string | null;
  boards: DashboardBoard[];
  activeBoardId: string;
  editing: boolean;
  /** 회사 기본판을 어느 회차까지 받았는가 (migration 173). */
  seenForced: Record<string, number>;
  /** 저장본이 아예 없던 사람인가 — «처음 여는 사람» 만 회사 첫 배치를 통째로 받는다. */
  firstVisit: boolean;
  /** 탭에서 숨긴 보드 (시안 화면 H). 지운 것이 아니라 접어 둔 것이라 되살릴 수 있다. */
  hiddenBoardIds: string[];
  /** 로그인 첫 화면으로 지정한 보드. */
  defaultBoardId: string | null;
  /** 회사가 «사용자 숨기기» 를 허용했는가 (migration 175). 서버 정책이 오기 전 기본은 허용. */
  canHideTabs: boolean;
  /** 회사가 «탭 순서 바꾸기» 를 허용했는가 (migration 175). */
  canReorderTabs: boolean;

  hydrate: (userId: string) => void;
  /** 회사 기본판을 반영한다 — 서버에서 읽어 온 뒤 한 번 부른다. */
  applyTemplates: (templates: CompanyTemplate[]) => void;

  /* ── 탭 (시안 화면 H) ─────────────────────────────────────── */
  hideBoard: (id: string) => void;
  unhideBoard: (id: string) => void;
  reorderBoards: (fromIndex: number, toIndex: number) => void;
  updateBoard: (id: string, patch: { name?: string; kind?: BoardKind; visibility?: BoardVisibility; color?: BoardColor }) => void;
  duplicateBoard: (id: string) => string | null;
  setDefaultBoard: (id: string | null) => void;
  setActiveBoard: (id: string) => void;
  toggleEditing: () => void;

  createBoard: (input: { name: string; kind: BoardKind; visibility: BoardVisibility; widgetKeys: string[] }) => string;
  removeBoard: (id: string) => void;

  moveWidget: (boardId: string, fromIndex: number, toIndex: number) => void;
  setWidgetSpan: (boardId: string, widgetId: string, span: WidgetSpan) => void;
  /** 위젯 설정 — 준 칸만 덮어쓴다(안 준 칸이 기본값으로 되돌아가지 않게). */
  setWidgetSettings: (boardId: string, widgetId: string, patch: WidgetSettings) => void;
  removeWidget: (boardId: string, widgetId: string) => void;
  /**
   * 위젯 담기. `spans` 가 있으면 그 폭으로 놓는다(D3 · 2026-09-16 — 담은 위젯이 기본 폭을 무시하고 1/3 로 들어가던 것).
   * ★ store 는 위젯 목록(컴포넌트)을 모른다 — 기본 폭은 부르는 쪽이 목록에서 읽어 넘긴다. 안 주면 종전대로 1/3.
   */
  addWidgets: (boardId: string, widgetKeys: string[], spans?: Partial<Record<string, WidgetSpan>>) => void;
}

export const useDashboardStore = create<DashboardState>((set, get) => {
  /** 변경 후 현재 상태를 그대로 영속. 모든 mutating action 의 꼬리에 붙는다. */
  const persist = () => {
    const s = get();
    if (s.hydratedFor === null) return;
    save(s.hydratedFor, s.boards, s.activeBoardId, s.seenForced, s.hiddenBoardIds, s.defaultBoardId ?? undefined);
    /* 로컬은 이미 저장됐다(M2 — 거울은 늘 남는다). 깃발이 켜져 있을 때만 모아서 서버에도 올린다. */
    schedulePush(s.hydratedFor, syncPorts);
  };

  /**
   * 동기화가 store 를 들여다보는 **유일한 창구** (V10).
   * ★ 서버 문서로 교체할 때 `hydratedFor` · `editing` 은 건드리지 않는다 — 누가 보고 있는지,
   *   편집 중인지는 **이 기기의 사정**이지 서버가 정할 것이 아니다.
   */
  const syncPorts: SyncPorts = {
    getSnapshot: () => {
      const s = get();
      return {
        activeBoardId: s.activeBoardId, boards: s.boards, seenForced: s.seenForced,
        hiddenBoardIds: s.hiddenBoardIds, defaultBoardId: s.defaultBoardId ?? undefined,
      };
    },
    hasLocal: () => (get().hydratedFor === null ? false : load(get().hydratedFor!) !== null),
    applyServerDoc: (doc) => {
      const s = get();
      /* 회사 판은 서버 문서에 없다(M4) — 지금 화면의 것을 그대로 둔다. */
      const corp = s.boards.filter((b) => b.kind === 'corp');
      const boards = [...corp, ...doc.boards];
      const activeBoardId = boards.some((b) => b.id === doc.activeBoardId)
        ? doc.activeBoardId
        : (boards.find((b) => b.kind === 'personal')?.id ?? boards[0]?.id ?? '');
      set({
        boards, activeBoardId,
        seenForced: doc.seenForced ?? {},
        hiddenBoardIds: doc.hiddenBoardIds ?? [],
        defaultBoardId: doc.defaultBoardId ?? null,
      });
      /* 서버에서 받은 것을 로컬 거울에도 남긴다 — 깃발을 끄면 이것이 즉시 돌아온다. */
      if (s.hydratedFor !== null) {
        save(s.hydratedFor, boards, activeBoardId, doc.seenForced, doc.hiddenBoardIds, doc.defaultBoardId);
      }
    },
  };

  /** boards 배열 중 한 보드만 교체 — 잠긴 보드는 변경하지 않는다. */
  const patchBoard = (boardId: string, fn: (b: DashboardBoard) => DashboardBoard) => {
    set((s) => ({
      boards: s.boards.map((b) => (b.id === boardId && !b.locked ? fn(b) : b)),
    }));
    persist();
  };

  return {
    hydratedFor: null,
    boards: defaultBoards(),
    activeBoardId: 'mine',
    editing: false,
    seenForced: {},
    firstVisit: false,
    hiddenBoardIds: [],
    defaultBoardId: null,
    canHideTabs: true,
    canReorderTabs: true,

    hydrate: (userId) => {
      if (get().hydratedFor === userId) return;
      const saved = load(userId);
      const boards = saved?.boards ?? defaultBoards();
      /* 보드가 하나도 없을 수 있다(처음 여는 사람) — 그때는 «고른 것 없음»이다. */
      const fallback = boards.find((b) => b.kind === 'personal')?.id ?? boards[0]?.id ?? '';
      // ★ 「기본으로 지정」한 보드가 있으면 거기서 시작한다(시안 화면 H). 없으면 마지막에 본 것.
      const pinned = saved?.defaultBoardId && boards.some((b) => b.id === saved.defaultBoardId)
        ? saved.defaultBoardId
        : null;
      const activeBoardId = pinned
        ?? (saved && boards.some((b) => b.id === saved.activeBoardId) ? saved.activeBoardId : fallback);
      set({
        hydratedFor: userId, boards, activeBoardId, editing: false,
        seenForced: saved?.seenForced ?? {},
        hiddenBoardIds: saved?.hiddenBoardIds ?? [],
        defaultBoardId: saved?.defaultBoardId ?? null,
        // ★ 저장본이 아예 없던 사람 = **처음 여는 사람**. 회사 기본판이 오면 그 사람만
        //   첫 배치를 통째로 받는다(이미 쓰던 사람의 배치는 건드리지 않는다).
        firstVisit: !saved,
      });
      /*
       * 화면은 **위에서 이미 그려졌다**(로컬 기준). 서버와 맞추는 일은 그 뒤에 따라온다 —
       * `hydrate` 는 동기 계약이라(부른 직후 상태를 읽는 곳이 여럿이다) 기다리지 않는다.
       * 깃발이 꺼져 있으면 이 줄은 첫 줄에서 돌아간다.
       */
      void syncOnHydrate(userId, syncPorts);
    },

    setActiveBoard: (id) => {
      set({ activeBoardId: id, editing: false });
      persist();
    },

    toggleEditing: () => set((s) => ({ editing: !s.editing })),

    createBoard: ({ name, kind, visibility, widgetKeys }) => {
      const board: DashboardBoard = {
        id: localId('b'),
        name,
        kind,
        visibility,
        widgets: widgetKeys.map((k) => w(k)),
      };
      set((s) => ({ boards: [...s.boards, board], activeBoardId: board.id, editing: false }));
      persist();
      return board.id;
    },

    removeBoard: (id) => {
      set((s) => {
        const target = s.boards.find((b) => b.id === id);
        if (!target || target.locked) return s;
        const boards = s.boards.filter((b) => b.id !== id);
        const first = boards[0];
        if (!first) return s;   // 마지막 보드는 지우지 않는다.
        return {
          boards,
          activeBoardId: s.activeBoardId === id ? first.id : s.activeBoardId,
        };
      });
      persist();
    },

    moveWidget: (boardId, fromIndex, toIndex) => {
      patchBoard(boardId, (b) => {
        if (
          fromIndex === toIndex
          || fromIndex < 0 || fromIndex >= b.widgets.length
          || toIndex < 0 || toIndex >= b.widgets.length
        ) return b;
        const widgets = [...b.widgets];
        const [moved] = widgets.splice(fromIndex, 1);
        if (!moved) return b;
        widgets.splice(toIndex, 0, moved);
        return { ...b, widgets };
      });
    },

    setWidgetSpan: (boardId, widgetId, span) => {
      patchBoard(boardId, (b) => ({
        ...b,
        widgets: b.widgets.map((x) => (x.id === widgetId ? { ...x, span } : x)),
      }));
    },

    /* ── 탭 (2026-09-08 · 시안 화면 H) ───────────────────────────────────── */

    /**
     * 탭에서 숨기기 — **지우는 것이 아니라 접는 것**이다.
     *
     * ★★★ 되살릴 길을 함께 두지 않으면 「숨기기」는 사실상 삭제다. 탭 줄 끝의 「숨긴 탭 N」이
     *     그 입구다(시안에는 없지만, 없으면 할 수 없는 일을 메뉴에 세우는 셈이 된다).
     * ★ 보고 있던 탭을 숨기면 다른 탭으로 옮겨 준다 — 빈 화면에 남겨 두지 않는다.
     */
    hideBoard: (id) => {
      set((s) => {
        if (!s.canHideTabs) return s;
        const hidden = s.hiddenBoardIds.includes(id) ? s.hiddenBoardIds : [...s.hiddenBoardIds, id];
        const visible = s.boards.filter((b) => !hidden.includes(b.id));
        // 마지막 하나까지 숨기지는 않는다 — 탭이 0 이면 갈 곳이 없다.
        if (visible.length === 0) return s;
        const activeBoardId = visible.some((b) => b.id === s.activeBoardId)
          ? s.activeBoardId
          : visible[0]!.id;
        return { hiddenBoardIds: hidden, activeBoardId };
      });
      persist();
    },

    unhideBoard: (id) => {
      set((s) => ({ hiddenBoardIds: s.hiddenBoardIds.filter((x) => x !== id) }));
      persist();
    },

    /**
     * 탭 순서 — **나에게만 적용된다**(시안의 곁말 그대로). 남의 탭 순서를 바꾸는 일이 아니다.
     * ★ 회사가 「맨 앞 고정」을 걸었으면(canReorderTabs=false) 아무 일도 하지 않는다.
     */
    reorderBoards: (fromIndex, toIndex) => {
      set((s) => {
        if (!s.canReorderTabs) return s;
        if (fromIndex === toIndex) return s;
        if (fromIndex < 0 || fromIndex >= s.boards.length) return s;
        if (toIndex < 0 || toIndex >= s.boards.length) return s;
        const boards = [...s.boards];
        const [moved] = boards.splice(fromIndex, 1);
        if (!moved) return s;
        boards.splice(toIndex, 0, moved);
        return { boards };
      });
      persist();
    },

    /** 이름 · 유형 · 공개 범위. 잠긴 보드(전사)는 그대로 둔다. */
    updateBoard: (id, patch) => {
      patchBoard(id, (b) => ({
        ...b,
        ...(patch.name !== undefined ? { name: patch.name } : {}),
        ...(patch.kind !== undefined ? { kind: patch.kind } : {}),
        ...(patch.visibility !== undefined ? { visibility: patch.visibility } : {}),
        ...(patch.color !== undefined ? { color: patch.color } : {}),
      }));
    },

    /**
     * 복제 — 배치까지 통째로.
     * ★ 위젯 인스턴스 id 는 **새로 만든다.** 그대로 두면 한쪽에서 폭을 바꿀 때 다른 쪽이 함께 바뀐다.
     * ★ 복제본은 잠기지 않는다 — 잠긴 전사 보드를 복제하면 «내가 고칠 수 있는 사본» 이 된다.
     */
    duplicateBoard: (id) => {
      const src = get().boards.find((b) => b.id === id);
      if (!src) return null;
      const copy: DashboardBoard = {
        id: localId('b'),
        name: `${src.name} 사본`,
        kind: src.kind === 'corp' ? 'personal' : src.kind,
        visibility: src.visibility,
        widgets: src.widgets.map((x) => ({ ...x, id: localId('w') })),
      };
      set((s) => ({ boards: [...s.boards, copy], activeBoardId: copy.id, editing: false }));
      persist();
      return copy.id;
    },

    /** 로그인 첫 화면. 다시 누르면 해제된다(«마지막에 본 것» 으로 돌아간다). */
    setDefaultBoard: (id) => {
      set((s) => ({ defaultBoardId: s.defaultBoardId === id ? null : id }));
      persist();
    },

    setWidgetSettings: (boardId, widgetId, patch) => {
      patchBoard(boardId, (b) => ({
        ...b,
        widgets: b.widgets.map((x) => (x.id === widgetId
          // ★ 준 칸만 덮는다 — 통째로 갈아 끼우면 이름만 고쳤는데 높이가 기본값으로 돌아간다.
          ? { ...x, settings: { ...x.settings, ...patch } }
          : x)),
      }));
    },

    removeWidget: (boardId, widgetId) => {
      patchBoard(boardId, (b) => ({ ...b, widgets: b.widgets.filter((x) => x.id !== widgetId) }));
    },

    addWidgets: (boardId, widgetKeys, spans) => {
      patchBoard(boardId, (b) => ({
        ...b,
        widgets: [...b.widgets, ...widgetKeys.map((k) => {
          const span = spans?.[k];
          return span && ([4, 6, 8, 12] as number[]).includes(span) ? w(k, span) : w(k);
        })],
      }));
    },

    /**
     * 회사 기본판 반영 (migration 173 · 시안 화면 I).
     *
     * ★★★ **이미 쓰고 있는 사람의 배치는 건드리지 않는다.** 시안이 그렇게 정했고, 그 편이
     *     되돌릴 수 없는 사고를 막는다. 개인 배치가 바뀌는 경우는 딱 둘뿐이다 —
     *       ① 저장본이 아예 없던 사람(처음 여는 사람)
     *       ② 관리자가 「덮어쓰기」를 누른 회차를 아직 안 받은 사람
     *
     * ★★ 전사 보드는 «회사의 것» 이라 늘 따라간다 — 잠겨 있어 사용자가 고칠 수 없으므로
     *    덮어써서 잃을 것이 없다. 껐으면(enabled=false) 탭에서 **사라진다.**
     */
    applyTemplates: (templates) => {
      const s = get();
      if (s.hydratedFor === null) return;
      const byKind = new Map(templates.map((t) => [t.kind, t]));
      const toWidgets = (t: CompanyTemplate) =>
        t.widgets.map((x) => w(x.key, ([4, 6, 8, 12] as number[]).includes(x.span) ? (x.span as WidgetSpan) : 4));

      let boards = [...s.boards];
      const seen = { ...s.seenForced };

      // ── 전사 보드 ────────────────────────────────────────────────
      /*
       * ★★★ **회사가 정한 것이 없으면 전사 탭도 없다** (2026-09-09).
       *   종전에는 `CORP` 기본판이 «있을 때만» 손을 댔다 — 그래서 회사가 아무것도 안 정한
       *   상태에서 앱에 박힌 전사 탭이 그대로 떠 있었다. 「전사」는 회사의 말이지 앱의 말이 아니다.
       */
      const corp = byKind.get('CORP');
      {
        const idx = boards.findIndex((b) => b.kind === 'corp');
        if (!corp || !corp.enabled) {
          if (idx >= 0) boards.splice(idx, 1);              // 안 정했거나 껐으면 탭에서 사라진다
        } else {
          const next: DashboardBoard = {
            id: idx >= 0 ? boards[idx]!.id : 'corp',
            name: corp.name,
            kind: 'corp',
            visibility: 'team',
            locked: true,
            widgets: toWidgets(corp),
          };
          if (idx >= 0) boards[idx] = next; else boards = [next, ...boards];
        }
      }

      /*
       * ── 개인 보드는 «건드리지 않는다» (2026-09-09 사용자 지시) ──────────────
       *
       * ★★★ 종전에는 회사의 「신규 사용자 기본」·「부서별 기본」이 **개인 대시보드를 채우고**,
       *     회사가 「덮어쓰기」를 걸면 각자 다음에 열 때 **제 배치가 갈아 끼워졌다.**
       *     그 길을 걷어냈다 — **개인 대시보드는 사용자의 것**이다. 회사가 정하는 것은
       *     잠긴 **전사 탭 하나**뿐이고, 각자 꾸민 배치는 그대로 남는다.
       * ★ 실측(2026-09-09 운영): `dashboard_templates` 에 행이 하나도 없었다 —
       *   그 두 기본판을 쓰던 회사가 0 이라 잃는 것도 없다.
       * ⚠ `seenForced` 는 **지우지 않고 둔다** — 옛 저장본에 남아 있는 값이라, 읽고 쓰는 자리만
       *   없앤다(저장 모양을 바꾸면 옛 저장본이 통째로 버려진다).
       */

      // ── 로그인 첫 화면 ───────────────────────────────────────────
      // 사용자가 개인 설정에서 다시 고르기 전까지의 **기본값**이다(시안 정책 절).
      let activeBoardId = s.activeBoardId;
      const landing = corp?.loginLanding ?? 'LAST';
      if (s.firstVisit || landing !== 'LAST') {
        if (landing === 'CORP') activeBoardId = boards.find((b) => b.kind === 'corp')?.id ?? activeBoardId;
        else if (landing === 'PERSONAL') activeBoardId = boards.find((b) => b.kind === 'personal')?.id ?? activeBoardId;
      }
      if (!boards.some((b) => b.id === activeBoardId)) {
        activeBoardId = boards.find((b) => b.kind === 'personal')?.id ?? boards[0]?.id ?? activeBoardId;
      }

      /**
       * 탭 정책 (migration 175) — 전사 기본판이 정한다.
       * ★ 서버가 안 주면 **허용**이 기본이다. 정책을 못 읽었다고 사람 손을 묶지 않는다.
       */
      const policy = corp;
      const canHideTabs = policy?.userCanHide ?? true;
      const canReorderTabs = (policy?.tabPosition ?? 'FREE') !== 'FIRST';

      // 회사가 숨기기를 잠갔으면 **이미 숨겨 둔 것도 되살린다** — 안 그러면 잠근 뒤에도
      // 누군가의 화면에서는 그 탭이 계속 사라진 채로 남는다.
      const hiddenBoardIds = canHideTabs ? s.hiddenBoardIds : [];

      set({ boards, activeBoardId, seenForced: seen, firstVisit: false, canHideTabs, canReorderTabs, hiddenBoardIds });
      persist();
    },
  };
});

/** 보드 유형 라벨 — 목록·모달이 같은 문구를 쓰도록 한곳에 둔다. */
export const BOARD_KIND_LABEL: Record<BoardKind, string> = {
  corp: '전사',
  personal: '개인',
  team: '부서',
  project: '프로젝트',
};

export const BOARD_VISIBILITY_LABEL: Record<BoardVisibility, string> = {
  private: '나만 보기',
  team: '부서 공유',
  members: '지정 멤버 공유',
};
